import { api } from "../lib/api";

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string | null;
  senderClientId?: string | null;
  senderLabel?: string | null;
  content: string;
  createdAt: string;
  reactions?: Array<{
    emoji: string;
    count: number;
    reactedByMe: boolean;
  }>;
  status?: "sending";
}

export type ConversationStatus = "OPEN" | "CLOSURE_PENDING" | "CLOSED";

export interface ConversationListItem {
  id: string;
  subject: string;
  status: ConversationStatus;
  createdAt: string;
  updatedAt: string;
  peerLabel?: string;
  lastMessage: ChatMessage | null;
  /** True si le dernier message du fil n’est pas couvert par la marque « vu » côté praticien (préférence serveur). */
  hasUnread?: boolean;
}

export interface ConversationSummary {
  id: string;
  subject: string;
  status: ConversationStatus;
  peerLabel?: string;
  hasClientParticipant: boolean;
}

export interface SendMessagePayload {
  content: string;
}

export interface ToggleReactionPayload {
  emoji: string;
}

export type DirectConversationPayload =
  | { participantUserId: string; subject?: string }
  | { participantClientId: string; subject: string };



export const fetchConversations = async (): Promise<ConversationListItem[]> => {
  const { data } = await api.get<ConversationListItem[]>("/conversations");
  return data;
}



export const findOrCreateDirectConversation = async (payload: DirectConversationPayload): Promise<{ id: string }> => {
  const { data } = await api.post<{ id: string }>("/conversations/direct", payload);
  return data;
}



export const fetchConversationSummary = async (conversationId: string): Promise<ConversationSummary> => {
  const { data } = await api.get<ConversationSummary>(
    `/conversations/${conversationId}`,
  );
  return data;
}



export const postRequestConversationClosure = async (conversationId: string): Promise<{ ok: boolean; status: "CLOSURE_PENDING" }> => {
  const { data } = await api.post<{ ok: boolean; status: "CLOSURE_PENDING" }>(
    `/conversations/${conversationId}/request-closure`,
  );
  return data;
}



export const postCancelConversationClosure = async (conversationId: string): Promise<{ ok: boolean; status: "OPEN" }> => {
  const { data } = await api.post<{ ok: boolean; status: "OPEN" }>(
    `/conversations/${conversationId}/cancel-closure`,
  );
  return data;
}



export const postCloseConversation = async (conversationId: string): Promise<{ ok: boolean; status: "CLOSED" }> => {
  const { data } = await api.post<{ ok: boolean; status: "CLOSED" }>(
    `/conversations/${conversationId}/close`,
  );
  return data;
}



export const postConversationSeenForStaff = async (conversationId: string): Promise<{ ok: boolean }> => {
  const { data } = await api.post<{ ok: boolean }>(
    `/conversations/${conversationId}/seen`,
  );
  return data;
}



export const fetchMessages = async (conversationId: string, limit: number = 100): Promise<ChatMessage[]> => {
  const { data } = await api.get<ChatMessage[]>(
    `/conversations/${conversationId}/messages`,
    { params: { limit } },
  );
  return data;
}



export const pollMessages = async (conversationId: string, after?: string): Promise<{ hasUpdates: boolean; updatedAt: string }> => {
  const { data } = await api.get<{ hasUpdates: boolean; updatedAt: string }>(
    `/conversations/${conversationId}/messages/poll`,
    { params: { after, timeoutMs: 25000 } },
  );
  return data;
}



export const postMessage = async (conversationId: string, payload: SendMessagePayload): Promise<ChatMessage> => {
  const { data } = await api.post<ChatMessage>(
    `/conversations/${conversationId}/messages`,
    payload,
  );
  return data;
}



export const addMessageReaction = async (conversationId: string, messageId: string, payload: ToggleReactionPayload): Promise<{ ok: boolean }> => {
  const { data } = await api.post<{ ok: boolean }>(
    `/conversations/${conversationId}/messages/${messageId}/reactions`,
    payload,
  );
  return data;
}



export const removeMessageReaction = async (conversationId: string, messageId: string, payload: ToggleReactionPayload): Promise<{ ok: boolean }> => {
  const { data } = await api.delete<{ ok: boolean }>(
    `/conversations/${conversationId}/messages/${messageId}/reactions?emoji=${encodeURIComponent(payload.emoji)}`,
  );
  return data;
}
